import { useEffect, useRef, useState } from "react";
import type { ApiClient } from "../../api/client";
import type { PricedLine } from "../../api/types";
import { t } from "../../i18n";
import { formatEuro, type Cents } from "../../lib/money";
import { formatTime } from "../../lib/time";
import { Dialog } from "../../ui/Dialog";
import { describeModifier, noteText } from "../sale/modifiers";

/** A guest's order from the card on a table that waits for the till (GET /device/table-qr/requests). */
interface TableQrRequest {
  id: string;
  table: string;
  name: string;
  lines: PricedLine[];
  totalCents: Cents;
  createdAt: string;
}

interface Props {
  api: ApiClient;
  /** Goes up when a request came in or was answered on another till (live), or the connection came back. */
  revision: number;
  toast: (message: string, tone?: "info" | "bad") => void;
  report: (error: unknown) => void;
}

/** A request is asked again this often, so one that expired on the server leaves the screen without a live message. */
const REFRESH_MS = 30_000;

/** A short double beep when a new request comes in: the window says what, the sound that something came. */
function beep(): void {
  try {
    const audio = new AudioContext();
    [0, 0.25].forEach((start) => {
      const tone = audio.createOscillator();
      const gain = audio.createGain();
      tone.frequency.value = 880;
      gain.gain.setValueAtTime(0.2, audio.currentTime + start);
      gain.gain.exponentialRampToValueAtTime(0.001, audio.currentTime + start + 0.18);
      tone.connect(gain).connect(audio.destination);
      tone.start(audio.currentTime + start);
      tone.stop(audio.currentTime + start + 0.2);
    });
    setTimeout(() => void audio.close(), 1_000);
  } catch {
    // no sound on this computer: the window still shows it
  }
}

/**
 * "Eerst goedkeuren aan de kassa" (QR-bestellen aan tafel; Furkan, 31/10/2026): every till shows the guests' orders
 * that wait, the oldest first, with the table, the guest's name, what he chose and the total. Goedkeuren sends it to
 * the table and the kitchen, Weigeren tells the guest to ask the staff. The window cannot be put away: someone has
 * to answer, here or on another till.
 */
export function TableQrRequests({ api, revision, toast, report }: Props) {
  const text = t.tableQrRequests;
  const [requests, setRequests] = useState<TableQrRequest[]>([]);
  const [busy, setBusy] = useState(false);
  const [tick, setTick] = useState(0);
  const seen = useRef(new Set<string>());

  useEffect(() => {
    const timer = setInterval(() => {
      setTick((count) => count + 1);
    }, REFRESH_MS);
    return () => {
      clearInterval(timer);
    };
  }, []);

  useEffect(() => {
    let stopped = false;
    api
      .request<{ requests: TableQrRequest[] }>("GET", "/device/table-qr/requests", { retry: true })
      .then((answer) => {
        if (stopped) return;
        const fresh = answer.requests.some((request) => !seen.current.has(request.id));
        for (const request of answer.requests) seen.current.add(request.id);
        setRequests(answer.requests);
        if (fresh) beep();
      })
      .catch(() => {
        // an older server or no connection: nothing waits on this screen
      });
    return () => {
      stopped = true;
    };
  }, [api, revision, tick]);

  const first = requests[0];
  if (!first) return null;

  const decide = async (accept: boolean) => {
    setBusy(true);
    try {
      await api.request("POST", `/device/table-qr/requests/${first.id}/${accept ? "accept" : "refuse"}`);
      setRequests((current) => current.filter((request) => request.id !== first.id));
      toast(accept ? text.accepted(first.table) : text.refused(first.table));
    } catch (error) {
      report(error);
      setTick((count) => count + 1);
    } finally {
      setBusy(false);
    }
  };

  const lineTitle = (line: PricedLine) =>
    `${String(line.quantity)}× ${line.name}${line.variant === null ? "" : ` ${line.variant}`}`;

  return (
    <Dialog
      title={text.title(first.table)}
      subtitle={text.from(first.name, formatTime(first.createdAt))}
      locked
      onClose={() => undefined}
      footer={
        <>
          {requests.length > 1 && <span className="note">{text.more(requests.length - 1)}</span>}
          <span className="spacer" />
          <button
            type="button"
            className="btn"
            disabled={busy}
            onClick={() => {
              void decide(false);
            }}
          >
            {text.refuse}
          </button>
          <button
            type="button"
            className="btn primary"
            data-autofocus
            disabled={busy}
            onClick={() => {
              void decide(true);
            }}
          >
            {text.accept}
          </button>
        </>
      }
    >
      <ul className="qrreq-lines">
        {first.lines.map((line, index) => (
          <li key={`${line.productId}-${String(index)}`}>
            <div>
              <b>{lineTitle(line)}</b>
              {(line.modifiers.length > 0 || line.note !== null) && (
                <small>
                  {[
                    ...line.modifiers.map((modifier) => describeModifier(modifier, false)),
                    ...(line.note === null ? [] : [noteText(line.note)]),
                  ].join(" · ")}
                </small>
              )}
            </div>
            <span className="num">{formatEuro(line.lineCents)}</span>
          </li>
        ))}
      </ul>
      <div className="qrreq-total">
        <span>{text.total}</span>
        <b className="num">{formatEuro(first.totalCents)}</b>
      </div>
    </Dialog>
  );
}
